// @flow

import { combineReducers } from 'redux';

import type { Reducer } from 'redux';

import { selectFromConfig } from '@gandi/redux-config';
import {
  createSequoiaResource,
  type DetailState,
  type ListState,
  type SequoiaResource,
  type SequoiaResourceType,
  type ResourceAction,
} from '@gandi/resource/redux';

import { getInstancesFqdn, getTrialId } from 'src/modules/gandi-studio';

import type { ThunkAction } from 'src/types';

const INSTANCE_STATUS_ACTIVE = 'active';
const INSTANCE_STATUS_BEING_CREATED = 'being_created';
const INSTANCE_STATUS_BEING_DELETED = 'being_deleted';
const INSTANCE_STATUS_BEING_MIGRATED = 'being_migrated';
const INSTANCE_STATUS_BEING_UPGRADED = 'being_upgraded';
const INSTANCE_STATUS_DELETED = 'deleted';
const INSTANCE_STATUS_LOCKED = 'locked';
const INSTANCE_STATUS_PAUSED = 'paused';
// `running` is a deprecated status API side. Replaced by `active`.
const INSTANCE_STATUS_RUNNING = 'running';
const INSTANCE_STATUS_WAITING_BILL = 'waiting_bill';
const INSTANCE_STATUS_WAITING_CONFIGURATION = 'waiting_configuration';
type InstanceStatus =
  | typeof INSTANCE_STATUS_BEING_CREATED
  | typeof INSTANCE_STATUS_BEING_DELETED
  | typeof INSTANCE_STATUS_BEING_MIGRATED
  | typeof INSTANCE_STATUS_BEING_UPGRADED
  | typeof INSTANCE_STATUS_DELETED
  | typeof INSTANCE_STATUS_LOCKED
  | typeof INSTANCE_STATUS_PAUSED
  | typeof INSTANCE_STATUS_ACTIVE
  | typeof INSTANCE_STATUS_RUNNING
  | typeof INSTANCE_STATUS_WAITING_BILL
  | typeof INSTANCE_STATUS_WAITING_CONFIGURATION;

const INSTANCE_BOOSTER_STATUS_BEING_CREATED = 'being_created';
const INSTANCE_BOOSTER_STATUS_ACTIVE = 'active';
const INSTANCE_BOOSTER_STATUS_BEING_DELETED = 'being_deleted';
const INSTANCE_BOOSTER_STATUS_DELETED = 'deleted';
type InstanceBoosterStatus =
  | typeof INSTANCE_BOOSTER_STATUS_BEING_CREATED
  | typeof INSTANCE_BOOSTER_STATUS_BEING_DELETED
  | typeof INSTANCE_BOOSTER_STATUS_ACTIVE
  | typeof INSTANCE_BOOSTER_STATUS_DELETED;

const INSTANCE_DB_LANGUAGE_STATUS_BETA = 'beta';
const INSTANCE_DB_LANGUAGE_STATUS_CURRENT = 'current';
const INSTANCE_DB_LANGUAGE_STATUS_DELETED = 'deleted';
const INSTANCE_DB_LANGUAGE_STATUS_DEPRECATED = 'deprecated';
const INSTANCE_DB_LANGUAGE_STATUS_HIDDEN = 'hidden';
type InstanceDBLanguageStatus =
  | typeof INSTANCE_DB_LANGUAGE_STATUS_BETA
  | typeof INSTANCE_DB_LANGUAGE_STATUS_CURRENT
  | typeof INSTANCE_DB_LANGUAGE_STATUS_DELETED
  | typeof INSTANCE_DB_LANGUAGE_STATUS_DEPRECATED
  | typeof INSTANCE_DB_LANGUAGE_STATUS_HIDDEN;

// legacy offers
const INSTANCE_SIZE_S = 's';
const INSTANCE_SIZE_S_PLUS = 's+';
const INSTANCE_SIZE_M = 'm';
const INSTANCE_SIZE_L = 'l';
const INSTANCE_SIZE_XL = 'xl';
const INSTANCE_SIZE_XXL = 'xxl';

// new offers
const INSTANCE_SIZE_ESSENTIAL = 'essential';
const INSTANCE_SIZE_STARTER = 'starter';
const INSTANCE_SIZE_ADVANCED = 'advanced';
const INSTANCE_SIZE_PRO = 'pro';
const INSTANCE_SIZE_BUSINESS = 'business';

type InstanceSize =
  | typeof INSTANCE_SIZE_ADVANCED
  | typeof INSTANCE_SIZE_BUSINESS
  | typeof INSTANCE_SIZE_ESSENTIAL
  | typeof INSTANCE_SIZE_L
  | typeof INSTANCE_SIZE_M
  | typeof INSTANCE_SIZE_PRO
  | typeof INSTANCE_SIZE_S
  | typeof INSTANCE_SIZE_S_PLUS
  | typeof INSTANCE_SIZE_STARTER
  | typeof INSTANCE_SIZE_XL
  | typeof INSTANCE_SIZE_XXL;

const INSTANCE_ACTION_CONSOLE = 'console';
const INSTANCE_ACTION_LOCK = 'lock';
const INSTANCE_ACTION_RESET_PASSWORD = 'reset_database_password';
const INSTANCE_ACTION_RESTART = 'restart';
const INSTANCE_ACTION_UPGRADE = 'upgrade';
const INSTANCE_ACTION_UNLOCK = 'unlock';
type InstanceAction =
  | typeof INSTANCE_ACTION_CONSOLE
  | typeof INSTANCE_ACTION_LOCK
  | typeof INSTANCE_ACTION_RESET_PASSWORD
  | typeof INSTANCE_ACTION_RESTART
  | typeof INSTANCE_ACTION_UPGRADE
  | typeof INSTANCE_ACTION_UNLOCK;

const INSTANCE_UPGRADE_MODE_DATABASE = 'database';
const INSTANCE_UPGRADE_MODE_LANGUAGE = 'language';
type InstanceUpgradeMode =
  | typeof INSTANCE_UPGRADE_MODE_DATABASE
  | typeof INSTANCE_UPGRADE_MODE_LANGUAGE;

type Database = {
  eol_at?: string,
  id: string,
  name: string,
  name_label: string,
  status: InstanceDBLanguageStatus,
  status_label: string,
  version?: string,
};

type Language = {
  ...Database,
  single_application: boolean,
};

type Storage = {
  unit: string,
  value: number,
};

interface InstanceBase {
  autorenew?: {
    duration: number,
    duration_type: string,
  };
  available_upgrade: boolean;
  commitment_ends_at?: string;
  created_at: string;
  database?: Database;
  datacenter?: {
    code: string,
    flags: Array<string>,
    name: string,
    region: string,
  };
  domain?: string;
  ended_at?: string;
  expire_at: string;
  free_booster_available?: boolean;
  id: string;
  language?: Language;
  name: string;
  password_updated_at?: string;
  sharing_space: {
    id: string,
    name: string,
    type: string,
  };
  size: InstanceSize;
  snapshot_enabled: boolean;
  status: InstanceStatus;
  status_label: string;
  storage: {
    additional: Storage,
    base: Storage,
    total: Storage,
  };
  subscription_cancelled?: boolean;
  subscription_periodicity?: {
    duration: number,
    duration_unit: 'month',
  };
  trial: boolean;
}

export type InstanceSummary = InstanceBase;

export interface Instance extends InstanceBase {
  access_information?: {
    admin_url: string,
    database: {
      admin_type: string,
      admin_url: string,
      admin_username?: string,
      host: string,
      port?: number,
      socket?: string,
    },
    sftp: {
      server: string,
      url: string,
    },
    user: string,
  };
  application_hint?: string;
  compatible_applications?: Array<{
    name: string,
    name_label: string,
    parameters?: Record<string, any>,
  }>;
  console?: {
    enabled: boolean,
    enabled_until?: string,
    url: string,
  };
  cpu: number;
  booster?: {
    current?: {
      cpu_override: number,
      created_at: string,
      expires_at: string,
      ram_override: {
        unit: string,
        value: number,
      },
      size_override: InstanceSize,
      status_label: string,
      status: InstanceBoosterStatus,
    },
    next?: {
      available: boolean,
      available_at?: string,
      cooldown_days: number,
      default_cpu_override: number,
      default_ram_override: {
        unit: string,
        value: number,
      },
      duration_days: number,
    },
  };
  ram: {
    unit: string,
    value: number,
  };
  upgrade_to: Array<{
    id: string,
    mode: InstanceUpgradeMode,
    name: string,
    name_label: string,
    version: string,
  }>;
  vhosts: Array<{
    fqdn: string,
    href: string,
  }>;
}

export type InstanceUpdatePayload = {
  booster?: boolean,
  data_disk_additional_size?: number,
  database_id?: string,
  language_id?: string,
  location?: string | null | void,
  name?: string,
  password?: string,
  size?: string,
  snapshot_enabled?: boolean,
  subscription_periodicity?: {
    duration: number,
    duration_unit: 'month',
  },
  type?: {
    database: {
      name: string,
      version: string,
    },
    language: {
      name: string,
      version: string,
    },
  } | null | void,
};

export type PostActionPayload = {
  action: InstanceAction,
  lock_storage?: boolean,
  lock_reason?: string,
};

export type State = {
  detail: DetailState<Instance>,
  list: ListState<InstanceSummary>,
};

type InstanceResourceType = SequoiaResourceType<{
  fetchResource: InstanceSummary,
}>;

export type InstancesResource = SequoiaResource<InstanceResourceType>;

export const shsResource: InstancesResource = createSequoiaResource(
  'shs',
  'instances',
  {
    fetchPath: 'simplehosting/instances',
    fetchOnePath: 'simplehosting/instances/{id}',
  },
);

const selectAppEndpoint = selectFromConfig<string>('app.endpoint', '/');

export const fetchTrialAndRedirectToConvert =
  (
    redirect: (url: string) => void,
    isServerSide: boolean = false,
  ): ThunkAction<Promise<((url: string) => void) | void>> =>
  async (dispatch, getState) => {
    try {
      const resp = await dispatch(
        shsResource.fetch(shsResource.options.fetchPath, {
          label: shsResource.computeLabel(shsResource.options.fetchPath, {}),
        }),
      );

      const id = resp.data && getTrialId(resp.data);
      if (!id) return;

      const response = await dispatch(
        shsResource.fetchOne(shsResource.options.fetchOnePath, {
          params: { id },
          label: shsResource.computeLabel(shsResource.options.fetchOnePath, {
            id,
          }),
        }),
      );

      const fqdn = response.data && getInstancesFqdn(response.data);
      if (!fqdn) return;
      const redirectTo = `/studio/${fqdn}/convert`;

      const endpoint = selectAppEndpoint(getState());
      const finalPath = isServerSide ? `${endpoint}${redirectTo}` : redirectTo;

      redirect(finalPath);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[handleRedirect] fetch failed', err);
    }
  };

export const reducer: Reducer<State, ResourceAction<>> = combineReducers({
  ...shsResource.listReducer,
});
